import { useEffect } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';

/* =============================================================================
 * The cache that survives the app being closed
 * -----------------------------------------------------------------------------
 * `networkMode: 'offlineFirst'` makes a query run without a connection so that
 * whatever is in the cache is served. That is exactly right, and it only helps
 * inside one session: the cache lives in memory, so an app that has been closed
 * and reopened has nothing in it. A supervisor arriving at a site with no signal
 * opened the app to empty screens, having had all of it an hour earlier.
 *
 * So a small part of the cache is written to the device.
 *
 * =============================================================================
 * WHAT IS NOT PERSISTED, AND WHY THAT IS MOST OF IT
 * =============================================================================
 * An allowlist, not a denylist. This is a multi-tenant application holding
 * payroll, and the failure mode of guessing wrong is a salary sitting in storage
 * on a phone that gets passed around a site office. A denylist is a promise to
 * remember every query key anybody adds in future; an allowlist is a decision
 * made on purpose, once, per key.
 *
 * What is on it is the reference data the app needs in order to work without a
 * signal: who is employed, which sites exist, where their fences are, what the
 * company rules say. Names and places.
 *
 * What is deliberately off it:
 *
 *   money      advances, payslips, payroll periods, salary history, job cost,
 *              settlements, adjustments. None of it is needed to mark somebody
 *              present, and all of it is why this is an allowlist.
 *
 *   one record  attendance days, leave requests, documents. These change
 *              constantly, and a stale one shown as current is worse than a
 *              screen admitting it cannot reach the server.
 *
 *   faces      face-templates is about 1.2 MB and already has a cache of its
 *              own on disk. Writing it here would exhaust the quota and take
 *              the rest of this with it.
 *
 *   platform   an operator's view of every company on the system is the last
 *              thing that should be left behind on a handset.
 * ========================================================================== */

const ALLOWED = [
  'employees',
  'designations',
  'projects',
  'project-options',
  'geofences',
  'holidays',
  'roles',
  'policy',
  'leave-types',
] as const;

const STORAGE_KEY = 'noorone.qc';

/* Bumped when the shape of what is stored changes. An old entry is discarded
   rather than migrated: this is a cache, and being wrong about its shape costs
   far more than one refetch. */
const VERSION = 1;

/* A shift. Long enough that somebody who worked yesterday opens the app today
   with the roster already on screen; short enough that a site which moved its
   fence last week is not still being drawn from last week. */
const MAX_AGE_MS = 12 * 60 * 60 * 1000;

/* Storage is roughly 5 MB for the whole origin, and the language, the theme,
   the sidebar state and the pending scan queue all live there too. This is a
   cache; it must never be the reason something that matters cannot be written. */
const MAX_BYTES = 1_000_000;

interface Stored {
  version: number;
  identity: string;
  savedAt: number;
  entries: Array<{ key: unknown[]; data: unknown; updatedAt: number }>;
}

/**
 * Who the cache belongs to.
 *
 * Both halves are load-bearing. The person, obviously. The company because a
 * platform operator can act as one company and then another inside a single
 * session, and what was cached in the first must not be readable in the second.
 * That is the tenancy boundary, and it does not stop being one because the data
 * took a detour through a handset.
 *
 * Null when nobody is signed in, which is treated as "do not restore and do not
 * save" rather than as an identity of its own.
 */
export function identityOf(
  userId: string | null | undefined,
  companyId: string | null | undefined,
): string | null {
  if (!userId || !companyId) return null;
  return `${userId}:${companyId}`;
}

function isAllowed(key: unknown): boolean {
  return Array.isArray(key)
    && typeof key[0] === 'string'
    && (ALLOWED as readonly string[]).includes(key[0]);
}

/** Forgets everything written, for any account. Called on sign-out. */
export function clearQueryCache(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* storage unavailable, in which case nothing was ever written */
  }
}

/**
 * Puts back what was saved, if it belongs to this person and is recent enough.
 *
 * The boolean is for the tests. The application behaves the same either way,
 * and a banner reading "restored from cache" explains an implementation detail
 * to somebody who wanted to know who is on site.
 */
export function restoreQueryCache(client: QueryClient, identity: string): boolean {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(STORAGE_KEY);
  } catch {
    return false;
  }
  if (!raw) return false;

  let stored: Stored;
  try {
    stored = JSON.parse(raw) as Stored;
  } catch {
    /* Corrupt, half written, or from a version that stored something else.
       Thrown away rather than reasoned about. */
    clearQueryCache();
    return false;
  }

  const tooOld = Date.now() - stored.savedAt > MAX_AGE_MS;

  if (stored.version !== VERSION || stored.identity !== identity || tooOld) {
    /* Removed, not merely ignored. A cache belonging to whoever used this
       handset last is precisely the thing that should not sit on it waiting for
       a later version of this function to decide it is readable. */
    clearQueryCache();
    return false;
  }

  let restored = 0;
  for (const entry of stored.entries ?? []) {
    if (!isAllowed(entry.key)) continue;

    /* updatedAt is passed through rather than set to now. React Query decides
       staleness from it, so a roster saved four hours ago is correctly treated
       as stale and refetched the moment there is a connection, while being on
       screen in the meantime. That is the entire point. */
    client.setQueryData(entry.key, entry.data, { updatedAt: entry.updatedAt });
    restored += 1;
  }

  return restored > 0;
}

/** Writes the allowlisted part of the cache. Silent on failure, by design. */
export function saveQueryCache(client: QueryClient, identity: string): void {
  const entries: Stored['entries'] = [];

  for (const query of client.getQueryCache().getAll()) {
    if (!isAllowed(query.queryKey)) continue;
    if (query.state.status !== 'success') continue;
    if (query.state.data === undefined) continue;

    entries.push({
      key: query.queryKey as unknown[],
      data: query.state.data,
      updatedAt: query.state.dataUpdatedAt,
    });
  }

  if (entries.length === 0) {
    clearQueryCache();
    return;
  }

  const payload: Stored = { version: VERSION, identity, savedAt: Date.now(), entries };

  let serialised: string;
  try {
    serialised = JSON.stringify(payload);
  } catch {
    /* Something in there is not serialisable. Not worth surfacing: the next
       load refetches. */
    return;
  }

  if (serialised.length > MAX_BYTES) {
    /* Over budget. Dropped entirely rather than truncated to whatever happened
       to come first: half a roster is worse than none, because it looks whole.
       The previous save goes too, being now the older of two wrong answers. */
    clearQueryCache();
    return;
  }

  try {
    localStorage.setItem(STORAGE_KEY, serialised);
  } catch {
    /* Quota, or a private window. The application works without this. */
  }
}

/* -----------------------------------------------------------------------------
 * Wiring
 * -----------------------------------------------------------------------------
 * Restore once, when the identity becomes known. Save when the app goes away.
 *
 * Not on every cache change. The cache changes on every keystroke of a search
 * box that filters a query, and serialising a roster on each one is a
 * synchronous JSON.stringify on the main thread while somebody is typing, which
 * is the sort of caching that costs more than it saves.
 *
 * visibilitychange to hidden is the moment that matters: on Android it is what
 * fires when the app is backgrounded, and an app backgrounded on a site is
 * often an app about to be killed. pagehide covers the browser.
 * -------------------------------------------------------------------------- */
export function useQueryCache(identity: string | null): void {
  const client = useQueryClient();

  useEffect(() => {
    if (!identity) return;

    restoreQueryCache(client, identity);

    const save = () => saveQueryCache(client, identity);

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') save();
    };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', save);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', save);
      /* Saved on the way out as well. This effect tears down when the identity
         changes, which is what an operator stepping into a different company
         does, and what is in the cache at that moment still belongs to the
         identity being left. */
      save();
    };
  }, [client, identity]);
}
